"use client"

import { useEffect, useState } from "react"
import { BiRefresh } from "react-icons/bi"
import { AiOutlineLoading3Quarters } from "react-icons/ai"
import { toast } from "sonner"
import { Button } from "@/components/ui-lab/button"

type PwaUpdatePromptProps = {
  autoReload?: boolean
}

export const PwaUpdatePrompt = ({ autoReload = false }: PwaUpdatePromptProps) => {
  const [updateReady, setUpdateReady] = useState(false)
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null)
  const [isUpdating, setIsUpdating] = useState(false)

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return

    let isMounted = true
    let currentController = navigator.serviceWorker.controller
    let refreshing = false

    navigator.serviceWorker.getRegistration().then((reg) => {
      if (!reg || !isMounted) return

      setRegistration(reg)

      if (reg.waiting) {
        setUpdateReady(true)
        if (autoReload) {
          handleAutoUpdate(reg)
        }
      }

      reg.addEventListener("updatefound", () => {
        const newWorker = reg.installing
        if (!newWorker) return

        newWorker.addEventListener("statechange", () => {
          if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
            if (isMounted) {
              setUpdateReady(true)
              if (autoReload) {
                handleAutoUpdate(reg)
              }
            }
          }
        })
      })
    })

    const handleControllerChange = () => {
      // The first install also fires controllerchange (clients.claim), but there is nothing to reload yet
      const isFirstClaim = !currentController
      currentController = navigator.serviceWorker.controller
      if (isFirstClaim || refreshing) return
      refreshing = true

      if (isMounted) {
        setUpdateReady(false)
        toast.success("App Updated", {
          description: "The app has been updated to the latest version.",
          duration: 3000,
        })
      }
      setTimeout(() => {
        window.location.reload()
      }, 500)
    }

    navigator.serviceWorker.addEventListener("controllerchange", handleControllerChange)

    return () => {
      isMounted = false
      navigator.serviceWorker.removeEventListener("controllerchange", handleControllerChange)
    }
  }, [autoReload])

  const handleAutoUpdate = (reg: ServiceWorkerRegistration) => {
    if (reg.waiting) {
      reg.waiting.postMessage({ type: "SKIP_WAITING" })
    }
  }

  const handleUpdate = () => {
    if (!registration?.waiting) return
    setIsUpdating(true)
    registration.waiting.postMessage({ type: "SKIP_WAITING" })
  }

  if (!updateReady || autoReload) return null

  return (
    <div className="fixed top-20 right-6 max-w-sm z-50 flex items-center gap-3 rounded-lg border border-blue-200 dark:border-blue-800 bg-white/95 dark:bg-gray-900/95 backdrop-blur px-4 py-3 shadow-xl">
      <div className="flex-1">
        <p className="text-sm font-medium text-gray-900 dark:text-white">Update available</p>
        <p className="text-xs text-gray-600 dark:text-gray-400">New version has been downloaded</p>
      </div>
      <Button size="sm" onClick={handleUpdate} disabled={isUpdating}>
        {isUpdating ? <AiOutlineLoading3Quarters size={14} className="animate-spin" /> : <BiRefresh size={14} />}
        {isUpdating ? "Updating..." : "Update"}
      </Button>
    </div>
  )
}
